import axios, { InternalAxiosRequestConfig } from 'axios';
import { isDev } from '@/types';

export const API_URL = isDev
  ? `http://${process.env.REACT_APP_BACKEND_HOST}:${process.env.REACT_APP_BACKEND_PORT}/api`
  : '/api';

// ---- DIAGNOSTICS -----
// console.log('%c[ENV DIAGNOSTICS]', 'color: #00f; font-weight: bold;');
// console.log('REACT_APP_BACKEND_HOST:', process.env.REACT_APP_BACKEND_HOST);
// console.log('REACT_APP_BACKEND_PORT:', process.env.REACT_APP_BACKEND_PORT);
// console.log('API_URL:', API_URL);

const $api = axios.create({
  withCredentials: true,
  baseURL: API_URL,
});

$api.interceptors.request.use(
  (
    config: InternalAxiosRequestConfig,
  ): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => {
    config.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`;
    return config;
  },
);

const notRefreshUrls = [
  '/login',
  '/logout',
  '/register',
  '/forgot',
  '/forgot-reset',
];

$api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !notRefreshUrls.includes(originalRequest.url)
    ) {
      originalRequest._retry = true;

      try {
        const response = await axios.post(
          `${API_URL}/refresh-token`,
          {},
          { withCredentials: true },
        );
        const { accessToken } = response.data;

        localStorage.setItem('accessToken', accessToken);
        $api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

        return $api(originalRequest);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const isEmailNotConfirmedError =
            err.response?.status === 403 &&
            err.response?.data?.error_code === 'EMAIL_NOT_CONFIRMED';

          if (isEmailNotConfirmedError) {
            // Refresh failed because email is not confirmed. This is expected.
            // Don't log out. Just reject the original request's promise.
            // The app should already be showing the "confirm email" banner.
            return Promise.reject(error);
          }
        }

        // For any other refresh failure, log the user out.
        localStorage.removeItem('accessToken');
        if (originalRequest.url !== '/login') {
          window.location.replace('/login');
        }
        return Promise.reject(err);
      }
    } else if (error.response?.status === 401 && originalRequest._retry) {
      localStorage.removeItem('accessToken');
      if (originalRequest.url !== '/login') {
        window.location.replace('/login');
      }
    }

    return Promise.reject(error);
  },
);

export default $api;
